import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { AlertTriangle, Loader2 } from "lucide-react";

/** Plays a go2rtc HLS stream (with native fallback for Safari). */
export function HlsPlayer({ src, flip }) {
  const videoRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | playing | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    let hls;
    setStatus("loading");

    if (Hls.isSupported()) {
      hls = new Hls({ liveDurationInfinity: true, lowLatencyMode: true, manifestLoadingMaxRetry: 2, maxBufferLength: 10 });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); setStatus("playing"); });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          setStatus("error");
          setMessage("Nu s-a putut încărca fluxul. Verificați că gateway-ul go2rtc rulează și camera este accesibilă.");
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", () => { video.play().catch(() => {}); setStatus("playing"); });
      video.addEventListener("error", () => { setStatus("error"); setMessage("Flux indisponibil."); });
    } else {
      setStatus("error");
      setMessage("Browserul nu suportă HLS.");
    }

    return () => { if (hls) hls.destroy(); };
  }, [src]);

  return (
    <div className="relative aspect-video w-full rounded-md bg-black overflow-hidden">
      <video ref={videoRef} muted playsInline controls className="w-full h-full object-contain" style={flip ? { transform: "rotate(180deg)" } : undefined} data-testid="hls-video" />
      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/70 bg-black/40">
          <Loader2 className="h-6 w-6 animate-spin" /><span className="text-xs">Se conectează la flux...</span>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 bg-black/70 p-4 text-center">
          <AlertTriangle className="h-6 w-6" /><span className="text-xs max-w-xs">{message}</span>
        </div>
      )}
      {status === "playing" && (
        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-0.5">
          <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /><span className="text-[10px] text-white font-medium">LIVE HLS</span>
        </div>
      )}
    </div>
  );
}
